<template>
  <div class="credits-page">
    <h1 class="credits-title">云币</h1>

    <section class="balance-card" aria-label="云币余额">
      <div class="balance-content">
        <strong class="balance-num">{{ balanceText }}</strong>
        <div class="balance-label">
          <span>云币余额</span>
          <SvgIcon name="ai-cost" :size="12" />
        </div>
      </div>

      <el-button class="buy-btn" type="primary" @click="onBuy">购买云币</el-button>
    </section>

    <section class="details-section" aria-label="云币明细">
      <div class="detail-tabs" role="tablist" aria-label="云币明细类型">
        <button
          v-for="tab in tabs"
          :key="tab.value"
          type="button"
          class="detail-tab"
          :class="{ active: activeTab === tab.value }"
          role="tab"
          :aria-selected="activeTab === tab.value"
          @click="activeTab = tab.value"
        >
          <span>{{ tab.label }}</span>
          <span class="detail-tab-indicator" aria-hidden="true" />
        </button>
      </div>

      <div v-if="activeTab === 'quantity'" class="quota-list">
        <div v-if="loading" class="quota-state">
          <el-skeleton :rows="3" animated />
        </div>

        <el-empty v-else-if="quotaItems.length === 0" description="暂无数量明细" :image-size="72" />

        <article v-for="item in quotaItems" v-else :key="item.id" class="quota-card">
          <div class="quota-header">
            <div class="quota-name">
              <SvgIcon name="orders-credits-cart" :size="16" />
              <span>{{ item.name }}</span>
            </div>
            <span class="quota-count">{{ item.used }} / {{ item.total }}</span>
          </div>
          <div class="quota-progress" aria-hidden="true">
            <span :style="{ width: `${item.percent}%` }" />
          </div>
          <div class="quota-meta">
            <span>数量: {{ item.total }}</span>
            <span>已使用: {{ item.used }}</span>
            <span>剩余: {{ item.remaining }}</span>
            <span>{{ item.expire }}</span>
          </div>
        </article>
      </div>

      <div v-else class="usage-panel">
        <div class="usage-table" role="table" aria-label="云币使用明细列表">
          <div class="usage-table__header" role="row">
            <span v-for="column in usageColumns" :key="column.key" role="columnheader">
              {{ column.label }}
            </span>
          </div>

          <div
            v-infinite-scroll="handleLoadMoreUsageItems"
            class="usage-table__body"
            role="rowgroup"
            :infinite-scroll-disabled="usageScrollDisabled"
            :infinite-scroll-distance="48"
          >
            <template v-if="usageItems.length">
              <div v-for="item in usageItems" :key="item.id" class="usage-table__row" role="row">
                <span role="cell">{{ item.id }}</span>
                <span role="cell">{{ item.time }}</span>
                <span role="cell">{{ item.type }}</span>
                <span role="cell">{{ item.amount }}</span>
                <span role="cell">{{ item.status }}</span>
              </div>
            </template>

            <div v-else class="usage-empty" role="row">
              {{ usageLoading ? '正在加载使用明细...' : '暂无使用明细' }}
            </div>

            <div v-if="usageItems.length && usageLoading" class="usage-list-state">
              正在加载更多...
            </div>

            <div v-else-if="usageItems.length && !hasMoreUsageItems" class="usage-list-state">
              没有更多明细了
            </div>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script setup lang="ts">
  import { computed, onMounted, ref, watch } from 'vue';
  import { storeToRefs } from 'pinia';
  import { ElMessage } from 'element-plus';
  import { fetchPointConsumeList } from '@/api/balance';
  import SvgIcon from '@/components/common/icon/SvgIcon.vue';
  import { useBalanceStore } from '@/store';
  import { useUpgradeModal } from '@/hooks';
  import type { PointConsumeRecord } from '@/api/balance';

  type DetailTab = 'quantity' | 'usage';

  const balanceStore = useBalanceStore();
  const { balance, balanceList, loading } = storeToRefs(balanceStore);

  interface QuotaItem {
    id: string;
    name: string;
    used: string;
    total: string;
    remaining: string;
    percent: number;
    expire: string;
  }

  interface UsageColumn {
    key: keyof UsageItem;
    label: string;
  }

  interface UsageItem {
    id: string;
    time: string;
    type: string;
    amount: string;
    status: string;
  }

  const activeTab = ref<DetailTab>('quantity');
  const usageItems = ref<UsageItem[]>([]);
  const usageLoading = ref(false);
  const usageLoaded = ref(false);
  const usagePage = ref(1);
  const usagePageSize = 20;
  const usageTotal = ref(0);
  let latestUsageRequestId = 0;

  const tabs: { label: string; value: DetailTab }[] = [
    { label: '数量明细', value: 'quantity' },
    { label: '使用明细', value: 'usage' }
  ];

  const usageColumns: UsageColumn[] = [
    { key: 'id', label: 'ID' },
    { key: 'time', label: '时间' },
    { key: 'type', label: '类型' },
    { key: 'amount', label: '云币变化（个）' },
    { key: 'status', label: '状态' }
  ];

  const balanceText = computed(() => formatNumber(balance.value));
  const hasMoreUsageItems = computed(
    () => !usageLoaded.value || usageItems.value.length < usageTotal.value
  );
  const usageScrollDisabled = computed(() => usageLoading.value || !hasMoreUsageItems.value);
  const quotaItems = computed<QuotaItem[]>(() => {
    const typeIndexes = new Map<number, number>();

    return balanceList.value.map((item, index) => {
      const type = Number(item.type ?? 0);
      const typeIndex = typeIndexes.get(type) ?? 0;
      typeIndexes.set(type, typeIndex + 1);

      const total = Number(item.total ?? 0) || 0;
      const remaining = Number(item.balance ?? 0) || 0;
      const used = Math.max(total - remaining, 0);

      return {
        id: `${item.id ?? index}-${type}-${typeIndex}`,
        name: getTypeLabel(type),
        used: formatNumber(used),
        total: formatNumber(total),
        remaining: formatNumber(remaining),
        percent: total > 0 ? Math.min((used / total) * 100, 100) : 0,
        expire: `到期时间: ${formatExpireTime(item.expire_time)}`
      };
    });
  });

  async function loadBalance() {
    const loaded = await balanceStore.refreshBalance();
    if (!loaded) ElMessage.error('云币余额获取失败，请稍后重试');
  }

  async function loadUsageItems(reset = false) {
    if (usageLoading.value) return;
    if (!reset && !hasMoreUsageItems.value) return;

    const page = reset ? 1 : usagePage.value;
    const requestId = latestUsageRequestId + 1;
    latestUsageRequestId = requestId;
    usageLoading.value = true;

    try {
      const data = await fetchPointConsumeList({
        page,
        pageSize: usagePageSize
      });
      if (requestId !== latestUsageRequestId) return;

      const mappedItems = data.list.map((item, index) =>
        mapUsageItem(item, (page - 1) * usagePageSize + index)
      );

      usageItems.value = reset ? mappedItems : [...usageItems.value, ...mappedItems];
      usageTotal.value = data.total;
      usagePage.value = page + 1;
      usageLoaded.value = true;
    } catch (error) {
      if (requestId !== latestUsageRequestId) return;

      ElMessage.error(getErrorMessage(error, '云币使用明细获取失败，请稍后重试'));
    } finally {
      if (requestId === latestUsageRequestId) usageLoading.value = false;
    }
  }

  function mapUsageItem(item: PointConsumeRecord, index: number): UsageItem {
    return {
      id: formatUsageId(item.id, index),
      time: formatApiTime(item.create_time),
      type: getConsumeTypeLabel(item.object_type),
      amount: formatUsageAmount(item.number),
      status: getConsumeStatusLabel(item.type)
    };
  }

  function handleLoadMoreUsageItems() {
    void loadUsageItems();
  }

  function getTypeLabel(type: number) {
    if (type === 0) return '会员套餐';
    if (type === 1) return '充值购买';
    if (type === 2) return '活动赠送';

    return '其他';
  }

  function getConsumeTypeLabel(objectType?: number | string) {
    const objectTypeNumber = Number(objectType);
    if (objectTypeNumber === 11) return '云展绘本生成图片';
    if (objectTypeNumber === 12) return '云展绘本生成角色';
    if (objectTypeNumber === 7) return '云展网AI智能排版';
    if (objectTypeNumber >= 0 && objectTypeNumber <= 10) return '云展网兑换服务';

    return '其他';
  }

  function getConsumeStatusLabel(type?: number | string) {
    const typeNumber = Number(type);
    if (typeNumber === 1) return '成功';
    if (typeNumber === 2) return '已返还';

    return '--';
  }

  function formatNumber(value: number) {
    return new Intl.NumberFormat('zh-CN', {
      maximumFractionDigits: 2
    }).format(value);
  }

  function formatUsageId(value: PointConsumeRecord['id'], index: number) {
    return toStringValue(value) || String(index + 1);
  }

  function formatUsageAmount(value?: number | string) {
    const numberValue = Number(value ?? 0);
    if (!Number.isFinite(numberValue)) return '--';

    return formatNumber(numberValue);
  }

  function formatApiTime(value?: number | string) {
    const timestamp = Number(value);
    if (!Number.isFinite(timestamp) || timestamp <= 0) return '--';

    const normalizedTimestamp = timestamp < 1_000_000_000_000 ? timestamp * 1000 : timestamp;
    const date = new Date(normalizedTimestamp);
    if (Number.isNaN(date.getTime())) return '--';

    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(
      date.getHours()
    )}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
  }

  function formatExpireTime(value?: number) {
    const timestamp = Number(value);
    if (!Number.isFinite(timestamp) || timestamp <= 0) return '永久';

    const normalizedTimestamp = timestamp < 1_000_000_000_000 ? timestamp * 1000 : timestamp;
    const date = new Date(normalizedTimestamp);
    if (Number.isNaN(date.getTime())) return '永久';
    if (date.getFullYear() === 2999) return '永久';

    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(
      date.getHours()
    )}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
  }

  function pad(value: number) {
    return String(value).padStart(2, '0');
  }

  function toStringValue(value: unknown) {
    if (typeof value === 'string') return value;
    if (typeof value === 'number') return String(value);
    return '';
  }

  function getErrorMessage(error: unknown, fallback: string) {
    if (typeof error === 'object' && error !== null) {
      const errorRecord = error as Record<string, unknown>;
      if (typeof errorRecord.errorMsg === 'string' && errorRecord.errorMsg) {
        return errorRecord.errorMsg;
      }
      if (typeof errorRecord.msg === 'string' && errorRecord.msg) return errorRecord.msg;
      if (typeof errorRecord.message === 'string' && errorRecord.message) {
        return errorRecord.message;
      }
    }

    if (error instanceof Error && error.message) return error.message;

    return fallback;
  }

  onMounted(() => {
    void loadBalance();
  });

  watch(activeTab, (tab) => {
    if (tab === 'usage' && !usageLoaded.value) void loadUsageItems(true);
  });

  const { showCoinBuy: onBuy } = useUpgradeModal();
</script>

<style scoped>
  .credits-page {
    --orders-body-bottom-space: 40px;

    height: calc(100% + var(--orders-body-bottom-space));
    margin-bottom: calc(var(--orders-body-bottom-space) * -1);
    min-height: 0;
    display: flex;
    flex-direction: column;
    gap: 22px;
  }

  .credits-title {
    flex-shrink: 0;
    margin: 0;
    color: #333940;
    font-size: 18px;
    font-weight: 700;
    line-height: 22px;
  }

  .balance-card {
    flex-shrink: 0;
    min-height: 107px;
    padding: 28px 59px 27px 55px;
    box-sizing: border-box;
    display: flex;
    align-items: center;
    justify-content: space-between;
    border-radius: 16px;
    background: #f2eff9;
  }

  .balance-content {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  .balance-num {
    color: #000000;
    font-size: 28px;
    font-weight: 700;
    line-height: 22px;
  }

  .balance-label {
    display: flex;
    align-items: center;
    gap: 8px;
    color: #000000;
    font-size: 16px;
    line-height: 22px;
  }

  .buy-btn {
    width: 120px;
    height: 42px;
    border: 0;
    border-radius: 16px;
    background: #6033e1;
    font-size: 13px;
    font-weight: 400;
  }

  .buy-btn:hover,
  .buy-btn:focus {
    background: #532bc8;
  }

  .details-section {
    flex: 1;
    min-height: 0;
    display: flex;
    flex-direction: column;
    gap: 16px;
  }

  .detail-tabs {
    flex-shrink: 0;
    display: flex;
    align-items: flex-start;
    gap: 20px;
  }

  .detail-tab {
    min-width: 56px;
    height: 24px;
    padding: 0;
    border: 0;
    background: transparent;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
    color: #6e6a7c;
    font-size: 14px;
    line-height: 14px;
    cursor: pointer;
    transition: color 0.15s;
  }

  .detail-tab.active,
  .detail-tab:hover {
    color: #160211;
  }

  .detail-tab-indicator {
    width: 20px;
    height: 2px;
    border-radius: 2px;
    background: #6033e1;
    opacity: 0;
    transition: opacity 0.15s;
  }

  .detail-tab.active .detail-tab-indicator {
    opacity: 1;
  }

  .quota-list {
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  .quota-state {
    padding: 16px;
    border: 1px solid #e8e8ee;
    border-radius: 8px;
    background: #ffffff;
  }

  .quota-card {
    padding: 16px;
    border: 1px solid #e8e8ee;
    border-radius: 8px;
    background: #ffffff;
    box-sizing: border-box;
  }

  .quota-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
  }

  .quota-name {
    display: flex;
    align-items: center;
    gap: 8px;
    color: #160211;
    font-size: 14px;
    font-weight: 500;
    line-height: 21px;
  }

  .quota-count {
    color: #6e6a7c;
    font-size: 14px;
    line-height: 21px;
  }

  .quota-progress {
    height: 8px;
    margin-top: 10px;
    overflow: hidden;
    border-radius: 999px;
    background: #e8e8ee;
  }

  .quota-progress span {
    display: block;
    height: 100%;
    border-radius: inherit;
    background: #6033e1;
  }

  .quota-meta {
    display: flex;
    align-items: center;
    gap: 24px;
    margin-top: 10px;
    color: #8a8795;
    font-size: 13px;
    line-height: 20px;
  }

  .usage-panel {
    flex: 1;
    min-height: 0;
    display: flex;
    width: 100%;
    overflow-x: auto;
    overflow-y: hidden;
  }

  .usage-table {
    flex: 1;
    display: flex;
    flex-direction: column;
    min-height: 0;
    min-width: 860px;
    width: 100%;
    overflow: hidden;
    color: #160211;
    font-size: 14px;
    line-height: 20px;
  }

  .usage-table__header,
  .usage-table__row {
    display: grid;
    grid-template-columns:
      minmax(120px, 0.7fr) minmax(180px, 1.15fr) minmax(180px, 1.15fr) minmax(126px, 0.7fr)
      minmax(72px, 0.4fr);
    align-items: center;
    column-gap: 32px;
    padding: 0 32px;
    box-sizing: border-box;
    width: 100%;
  }

  .usage-table__header {
    flex-shrink: 0;
    height: 44px;
    border-radius: 12px;
    background: #f2eff9;
    color: #6e6a7c;
    font-weight: 400;
  }

  .usage-table__body {
    flex: 1;
    min-height: 0;
    overflow-x: hidden;
    overflow-y: auto;
  }

  .usage-table__row {
    height: 72px;
    border-bottom: 1px solid #eff0f7;
  }

  .usage-table__header span,
  .usage-table__row span {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .usage-empty {
    height: 100%;
    min-height: 160px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #6e6a7c;
    font-size: 14px;
  }

  .usage-list-state {
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 48px;
    color: #8a8795;
    font-size: 13px;
  }

  @media (max-width: 767px) {
    .credits-page {
      --orders-body-bottom-space: 15px;

      gap: 16px;
    }

    .balance-card {
      min-height: 96px;
      padding: 20px;
    }

    .balance-num {
      font-size: 24px;
    }

    .buy-btn {
      width: 104px;
    }

    .quota-meta {
      align-items: flex-start;
      flex-direction: column;
      gap: 4px;
    }

    .usage-table {
      min-width: 500px;
      font-size: 12px;
      line-height: 18px;
    }

    .usage-table__header,
    .usage-table__row {
      grid-template-columns:
        minmax(64px, 0.55fr) minmax(128px, 1.25fr) minmax(118px, 1.15fr) minmax(74px, 0.7fr)
        minmax(40px, 0.4fr);
      column-gap: 8px;
      padding: 0 12px;
    }

    .usage-table__header {
      height: 36px;
      border-radius: 8px;
    }

    .usage-table__row {
      height: 54px;
    }
  }
</style>
